import React, { useState } from 'react';
import { Button } from './ui/button';
import { X, Check, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { i18n, tb } from '../utils/i18n';

interface EventEndedNoticePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onDontShowAgain: () => void;
}

export function EventEndedNoticePopup({ isOpen, onClose, onDontShowAgain }: EventEndedNoticePopupProps) {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  const handleDontShowAgain = () => {
    onDontShowAgain();
    handleClose();
    toast.success(i18n.t('messageHiddenToast'));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-2xl transform transition-all duration-300 ${
        isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
      }`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">{tb('event2NoticeTitle')}</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-5">
            <div className="space-y-4 text-base text-gray-700 dark:text-gray-300 leading-relaxed text-left">
              <p>{tb('event2NoticeP1')}</p>
              <p>{tb('event2NoticeP2')}</p>
              <p>{tb('event2NoticeP3')}</p>
              <p className="pt-2 text-gray-600 dark:text-gray-400 font-medium">
                {i18n.t('event2NoticeThanks')}
              </p>
            </div>
          </div>

          <div className="flex space-x-3 pt-2">
            <Button
              onClick={handleDontShowAgain}
              variant="outline"
              className="flex-1 h-10 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl"
            >
              <Check className="w-4 h-4 mr-2" />
              {i18n.t('dontShowAgain')}
            </Button>
            <Button
              onClick={handleClose}
              variant="outline"
              className="flex-1 h-10 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl"
            >
              {i18n.t('close')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
